import { useEffect, useState, type FormEvent } from 'react';
import {
  useMeQuery, useWorkspaceQuery, useWorkspacesQuery, useLoginMutation, useLogoutMutation,
  useSwitchWorkspaceMutation,
} from './api.js';
import { Alert, Card, Spinner } from './ui.js';
import { Capture } from './Capture.js';
import { Dashboard } from './Dashboard.js';
import { HelpSupport } from './HelpSupport.js';
import { GrowWithRoiPros, GrowthDiscovery } from './GrowWithRoiPros.js';

/** Two permanent places outside the four tabs: their own addresses, linked from the sidebar and footer. */
type Place = { kind: 'help' } | { kind: 'grow'; step: 'interests' | 'consult' } | null;
const placeFromPath = (pathname: string): Place => {
  const p = pathname.replace(/\/+$/, '').toLowerCase();
  return p === '/help' ? { kind: 'help' } : p === '/grow' ? { kind: 'grow', step: 'interests' } : null;
};
/** Verified in this repository (the public site already links it). ROI Pros' own address is the owner's to supply. */
const LEVELUP_URL = 'https://roilevelup.com';
import { CompanionConnect } from './CompanionConnect.js';
import { Logo, PRODUCT_NAME } from './brand.js';
import { BellIcon, CameraGlyph, HomeIcon, ReviewIcon, SettingsIcon } from './icons.js';
import { Review } from './Review.js';
import { Settings } from './Settings.js';
import { SupportDiagnostics } from './SupportDiagnostics.js';
import { MailboxReconnectBanner } from './MailboxReconnectBanner.js';
import {
  TABS, DEFAULT_REVIEW_FILTERS, initialLocation, pathFor,
  type ReviewFilters, type TabId, type SettingsTabId,
} from './navigation.js';
import { useReviewCountQuery } from './api.js';

function SignIn() {
  const [login, { isLoading, error }] = useLoginMutation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await login({ email, password });
  };

  return (
    <main className="grid min-h-screen place-items-center bg-gradient-to-br from-cp-sky via-cp-bg to-cp-mint p-4 sm:p-6">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex justify-center"><Logo size={40} /></div>
        <form onSubmit={onSubmit} className="space-y-4 rounded-card border border-cp-border bg-white p-6 shadow-lift">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Sign in</h1>
            <p className="mt-1 text-sm text-slate-600">Welcome back. Sign in to your {PRODUCT_NAME} workspace.</p>
          </div>
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Email</span>
            <input type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-cp-blue-600 focus:outline-none focus:ring-2 focus:ring-cp-blue-600/25" />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Password</span>
            <input type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-cp-blue-600 focus:outline-none focus:ring-2 focus:ring-cp-blue-600/25" />
          </label>
          {error ? <Alert tone="error">Email or password is incorrect.</Alert> : null}
          <button type="submit" disabled={isLoading}
            className="min-h-11 w-full rounded-xl bg-cp-blue-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-cp-blue-700 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-blue-600">
            {isLoading ? 'Signing in…' : 'Sign in'}
          </button>
          {/*
            Accounts are created by invitation today (SELF_SERVICE_REGISTRATION is off on
            staging). Public sign-up is planned through LevelUp Central, which has email
            verification and password recovery; docs/contactpilot/authentication.md.
          */}
          <p className="text-center text-xs text-slate-500">{PRODUCT_NAME} is available by invitation.</p>
        </form>
      </div>
    </main>
  );
}

/** Only rendered when the user actually belongs to more than one workspace. */
function WorkspacePicker({ activeId }: { activeId: string | null }) {
  const { data } = useWorkspacesQuery();
  const [switchWorkspace, { isLoading }] = useSwitchWorkspaceMutation();
  const list = data?.workspaces ?? [];
  if (list.length < 2) return null;

  return (
    <label className="text-sm">
      <span className="sr-only">Workspace</span>
      <select
        value={activeId ?? ''}
        disabled={isLoading}
        onChange={(e) => { void switchWorkspace({ workspaceId: e.target.value }); }}
        className="min-h-11 w-full rounded-xl border border-slate-300 bg-white px-2 py-1.5 text-sm"
      >
        {list.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
      </select>
    </label>
  );
}

export function App() {
  const { data: me, isLoading } = useMeQuery();
  const { data: workspace } = useWorkspaceQuery(undefined, { skip: !me });
  const [logout] = useLogoutMutation();
  /*
   * Returning from a consent screen lands on this page with `?gmail=…` or
   * `?microsoft=…`. The tab has to follow, or the person arrives at the overview with no
   * sign that anything happened and the outcome banner mounts on a screen nobody is
   * looking at.
   */
  const [initial] = useState(() => initialLocation(window.location.search, window.location.pathname));
  const [tab, setTabState] = useState<TabId>(initial.tab);
  const [settingsTab, setSettingsTabState] = useState<SettingsTabId>(initial.settings);
  // Review's filters live in the address too, so a filtered view can be bookmarked.
  const [reviewFilters, setReviewFilters] = useState<ReviewFilters>(initial.review ?? DEFAULT_REVIEW_FILTERS);
  /*
   * The hidden support route is left the moment somebody uses the navigation. It is
   * never linked from anywhere a customer looks.
   */
  const [support, setSupport] = useState(initial.support === 'diagnostics');
  // Only the old privacy link opens the disclosure, and only on arrival.
  const [privacyOpen, setPrivacyOpen] = useState(initial.privacyOpen === true);
  const [place, setPlaceState] = useState<Place>(() => placeFromPath(window.location.pathname));
  const openPlace = (p: Exclude<Place, null>) => {
    setPlaceState(p); setSupport(false);
    try { window.history.replaceState({}, '', p.kind === 'help' ? '/help' : '/grow'); } catch { /* the address is a convenience */ }
  };

  /*
   * Navigation writes the address it lands on, so a refresh stays put and an address
   * copied from the bar opens the same section. Only on a person's own click: on first
   * load the address may still carry a consent-callback parameter that the outcome banner
   * has to read first.
   */
  const go = (next: { tab: TabId; settings?: SettingsTabId; review?: ReviewFilters }) => {
    const settings = next.settings ?? settingsTab;
    const review = next.review ?? reviewFilters;
    setSupport(false);
    setPrivacyOpen(false);
    setPlaceState(null);
    setTabState(next.tab);
    setSettingsTabState(settings);
    setReviewFilters(review);
    try {
      window.history.replaceState({}, '', pathFor({ tab: next.tab, settings, review }));
    } catch { /* an address that cannot be rewritten is no reason to stop navigating */ }
  };
  /*
   * An old Review address (`?state=ready`) is shown at the address it now means
   * (`?view=needs_review`), so the bar never disagrees with the chip that is selected.
   * Review only: no consent callback ever returns there.
   */
  useEffect(() => {
    if (initial.tab !== 'review' || initial.support) return;
    try {
      window.history.replaceState({}, '', pathFor({ tab: 'review', settings: initial.settings, review: initial.review }));
    } catch { /* the address is a convenience; Review works without it */ }
  }, [initial]);
  const setTab = (t: TabId) => go({ tab: t });
  const openSettings = (s: SettingsTabId) => go({ tab: 'settings', settings: s });
  const openReview = (review: ReviewFilters) => go({ tab: 'review', review });

  /*
   * A real count, or nothing at all.
   *
   * The badge exists to say "something is waiting", so a zero would be noise — and an
   * invented number would be worse than noise.
   *
   * One number from one table. It used to be the length of the email queue, which
   * silently stopped being the truth the moment a CSV row could need a person too; and
   * the obvious fix — add the intake count to the email count — would have double-counted
   * every intake decision, because an intake item that needs attention IS a review task.
   * So the server counts tasks, and this asks for that count.
   *
   * `attention` is exactly Review's Needs review count across every source: something to
   * fix, something to approve, or a send to retry. Waiting, sent and closed rows are not
   * work, and the old task `total` is a different number, so it is never a fallback.
   */
  const review = useReviewCountQuery(undefined, { skip: !me, pollingInterval: 30_000 });
  const pending = review.data?.attention ?? 0;

  if (isLoading) {
    return <main className="grid min-h-screen place-items-center bg-cp-bg"><Spinner /></main>;
  }
  if (!me) return <SignIn />;
  // The Browser Companion's connect step: its own page, outside the app shell.
  if (window.location.pathname.replace(/\/+$/, '') === '/companion/connect') {
    return <CompanionConnect me={me} workspaceName={workspace?.name ?? null} />;
  }

  const ICON: Record<TabId, JSX.Element> = {
    home: <HomeIcon />, capture: <CameraGlyph />, review: <ReviewIcon />, settings: <SettingsIcon />,
  };
  const isCurrent = (id: TabId) => !support && !place && tab === id;
  const badge = (id: TabId, compact = false) => (id === 'review' && pending > 0 ? (
    <span
      className={`rounded-full bg-cp-blue-600 px-1.5 py-0.5 text-xs font-semibold leading-none text-white ${compact ? 'absolute -right-3 -top-8' : 'ml-auto'}`}
      aria-label={`${pending} need your attention`}
    >
      {pending}
    </span>
  ) : null);

  return (
    <div className="min-h-screen bg-cp-bg lg:flex">
      {/*
        Desktop: a sidebar with the four places, the workspace, and whose session this is.
        Below lg the same four places move to a bottom bar a thumb can reach.
      */}
      <aside className="hidden lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-64 lg:shrink-0 lg:flex-col lg:border-r lg:border-cp-border lg:bg-white/80 lg:backdrop-blur">
        <div className="px-6 pb-4 pt-6"><Logo /></div>
        <nav aria-label="Main" className="flex-1 px-3">
          <ul className="space-y-1">
            {TABS.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => setTab(t.id)}
                  aria-current={isCurrent(t.id) ? 'page' : undefined}
                  className={`flex min-h-11 w-full items-center gap-3 rounded-xl px-3 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-blue-600 ${
                    isCurrent(t.id) ? 'bg-cp-blue-50 text-cp-blue-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  {ICON[t.id]}
                  {t.label}
                  {badge(t.id)}
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-4 border-t border-cp-border px-3 pt-3 text-sm">
            <button type="button" aria-current={place?.kind === 'help' ? 'page' : undefined} onClick={() => openPlace({ kind: 'help' })}
              className={`flex min-h-11 w-full items-center rounded-md px-3 text-left font-medium ${place?.kind === 'help' ? 'bg-cp-blue-50 text-cp-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}>Help &amp; Support</button>
            <button type="button" aria-current={place?.kind === 'grow' ? 'page' : undefined} onClick={() => openPlace({ kind: 'grow', step: 'interests' })}
              className={`flex min-h-11 w-full items-center rounded-md px-3 text-left font-medium ${place?.kind === 'grow' ? 'bg-cp-blue-50 text-cp-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}>Grow with ROI Pros</button>
          </div>
        </nav>
        <div className="space-y-3 border-t border-cp-border p-4">
          <WorkspacePicker activeId={me.workspaceId} />
          <div className="min-w-0 text-xs leading-tight text-slate-500">
            <span className="block truncate font-medium text-slate-700" title={me.email ?? undefined}>{me.email ?? 'Signed in'}</span>
            {me.role ? <span className="block">{me.role} · {workspace?.name ?? 'Workspace'}</span> : null}
          </div>
          <button
            type="button"
            onClick={() => void logout()}
            className="inline-flex min-h-11 w-full items-center justify-center rounded-xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-blue-600"
          >
            Sign out
          </button>
        </div>
      </aside>

      <div className="min-w-0 flex-1">
      <header className="sticky top-0 z-30 border-b border-cp-border bg-white/85 backdrop-blur lg:hidden">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-2 sm:px-6">
          {/* One row at every width: a long workspace name truncates rather than pushing Sign out onto a line of its own. */}
          <div className="flex min-w-0 items-center gap-2">
            <span className="shrink-0"><Logo compact size={30} /></span>
            <span className="min-w-0 truncate text-sm font-medium text-slate-700" title={workspace?.name ?? undefined}>{workspace?.name ?? 'Workspace'}</span>
          </div>
          <div className="flex shrink-0 items-center gap-1 sm:gap-3">
            <div className="hidden sm:block"><WorkspacePicker activeId={me.workspaceId} /></div>
            {/*
              Whose session this is, on every screen.

              Not decoration. A browser one person has already signed into looks exactly
              like a fresh one, which is how a second person at a demo came to start
              connecting a mailbox from inside the first person's session — with that
              person's admin rights, against that person's workspace. Naming the signed-in
              account is what makes "sign out first" an obvious thing to do rather than
              something you only think of afterwards.
            */}
            <span className="hidden text-right text-xs leading-tight text-slate-500 sm:block">
              <span className="block font-medium text-slate-700">{me.email ?? 'Signed in'}</span>
              {me.role ? <span className="block">{me.role}</span> : null}
            </span>
            {/* The one notification there is: work waiting in Review. A real count or nothing. */}
            <button
              type="button"
              onClick={() => setTab('review')}
              aria-label={pending > 0 ? `${pending} need your attention — open Review` : 'Nothing needs your attention — open Review'}
              className="relative inline-flex h-11 w-11 items-center justify-center rounded-xl text-slate-600 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-blue-600"
            >
              <BellIcon />
              {pending > 0 ? <span className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full bg-cp-blue-600 ring-2 ring-white" aria-hidden="true" /> : null}
            </button>
            {/*
              A real target, not 53x20 of underlined text.

              Signing out is the control that stops one person's session being used by
              the next person at a demo, and on a phone it was the smallest thing on the
              screen. `min-h-11` is the same floor the primary navigation already uses.
            */}
            <button
              type="button"
              onClick={() => void logout()}
              className="-mr-2 inline-flex min-h-11 items-center px-2 text-sm text-slate-600 underline"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <nav aria-label="Main" className="fixed inset-x-0 bottom-0 z-30 border-t border-cp-border bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden">
        <ul className="mx-auto grid max-w-xl grid-cols-4">
          {TABS.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => setTab(t.id)}
                aria-current={isCurrent(t.id) ? 'page' : undefined}
                className={`flex min-h-14 w-full flex-col items-center justify-center gap-0.5 text-xs font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-cp-blue-600 ${
                  isCurrent(t.id) ? 'text-cp-blue-700' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <span className={`relative grid h-7 w-12 place-items-center rounded-full ${isCurrent(t.id) ? 'bg-cp-blue-50' : ''}`}>
                  {ICON[t.id]}
                </span>
                {/* The label first, so the button's accessible name begins with where it goes. */}
                <span className="relative">
                  {t.label}
                  {badge(t.id, true)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <main className="mx-auto max-w-6xl px-4 pb-28 pt-6 sm:px-6 lg:px-8 lg:pb-10 lg:pt-8">
        {!me.workspaceId ? (
          <Card className="p-6">
            <Alert tone="error">
              This account is not a member of any workspace, so there is nothing to show.
            </Alert>
          </Card>
        ) : (
          <>
            {/* Not on the Connections card itself, which says the same thing beside its Reconnect. */}
            {!(tab === 'settings' && settingsTab === 'connections' && !support) ? (
              <MailboxReconnectBanner
                canReconnect={me.role === 'owner' || me.role === 'admin'}
                onOpenConnections={() => openSettings('connections')}
              />
            ) : null}
            {support ? <SupportDiagnostics role={me.role} /> : null}
            {!support && place?.kind === 'help' ? <HelpSupport /> : null}
            {!support && place?.kind === 'grow' ? <GrowWithRoiPros key={place.step} initialStep={place.step} /> : null}
            {!support && !place && tab === 'home' ? <div className="mb-4"><GrowthDiscovery onOpen={(step) => openPlace({ kind: 'grow', step })} /></div> : null}
            {!support && !place && tab === 'home' ? (
              <Dashboard
                me={me}
                workspaceName={workspace?.name ?? null}
                onNavigate={(to) => {
                  if (to === 'capture' || to === 'review') setTab(to);
                  else if (to === 'review-sent') openReview({ view: 'sent', source: 'all' });
                  else openSettings(to);
                }}
              />
            ) : null}
            {!support && !place && tab === 'capture' ? (
              <Capture
                onOpenConnections={() => openSettings('connections')}
                onOpenCrm={() => openSettings('crm-tags')}
                onOpenCaptureSettings={() => openSettings('email-capture')}
                onOpenReview={(source) => openReview({ view: 'all', source })}
              />
            ) : null}
            {!support && !place && tab === 'review' ? (
              <Review
                filters={reviewFilters}
                onFiltersChange={openReview}
                onOpenCaptureSettings={() => openSettings('email-capture')}
                onOpenCrm={() => openSettings('crm-tags')}
              />
            ) : null}
            {!support && !place && tab === 'settings' ? (
              <Settings
                tab={settingsTab}
                onTabChange={openSettings}
                privacyOpen={privacyOpen}
              />
            ) : null}
          </>
        )}
        <footer className="mt-10 border-t border-cp-border pt-4 text-xs text-slate-500" data-app-footer="">
          <nav aria-label="More" className="flex flex-wrap gap-x-4 gap-y-2">
            <button type="button" className="min-h-11 underline-offset-2 hover:underline" onClick={() => openPlace({ kind: 'help' })}>Help &amp; Support</button>
            <button type="button" className="min-h-11 underline-offset-2 hover:underline" onClick={() => openPlace({ kind: 'grow', step: 'interests' })}>Grow with ROI Pros</button>
          </nav>
          <p className="mt-2">
            <a className="underline-offset-2 hover:underline" href={LEVELUP_URL} target="_blank" rel="noopener noreferrer">Powered by LevelUp</a>
            {' · '}A product by ROI Pros
          </p>
        </footer>
      </main>
      </div>
    </div>
  );
}
